import React, { useState, useEffect } from "react";
import { Modal, Input, Typography } from "antd";

const { Text } = Typography;

/**
 * Opens a configurable confirmation modal.
 *
 * Supports:
 * - Simple confirmation (no input)
 * - Reason-based confirmation with validation
 * - Dynamic titles, descriptions, and button styling
 *
 * This is NOT a React component — it is a function that triggers a modal.
 *
 * ---
 * Usage Patterns:
 *
 * Simple confirm:
 *   confirmAction({
 *     title: "Discard changes?",
 *     description: "All unsaved changes will be lost.",
 *     onOk: () => navigate("/")
 *   });
 *
 * Confirm with reason:
 *   confirmAction({
 *     title: "Reject submission?",
 *     requireReason: true,
 *     minReasonLength: 20,
 *     onOk: ({ reason }) => reject(reason)
 *   });
 *
 * ---
 *
 * @function confirmAction
 *
 * @param {Object} config - Configuration object
 * @param {string|React.ReactNode} config.title Title shown in the modal
 * @param {string|React.ReactNode} [config.description] Optional description text displayed above content
 * @param {boolean} [config.requireReason=false] Whether a reason input field should be shown
 * @param {number} [config.minReasonLength=0] Minimum characters required if reason is enabled
 * @param {function} config.onOk Callback executed when user confirms. Receives: { reason: string | null } Can return a Promise
 * @param {string} [config.okText="Confirm"] Confirm button text
 * @param {string} [config.cancelText="Cancel"] Cancel button text
 * @param {boolean} [config.danger=false]  Whether to style confirm button as danger
 * @param {boolean} [config.centered=true] Whether modal is centered
 * @param {Object} [config.modalProps] Additional AntD Modal.confirm props (advanced usage)
 *
 * @returns {void}
 */

export const confirmAction = ({
  title,
  description,
  onOk,
  requireReason = false,
  minReasonLength = 0,
  okText = "Confirm",
  cancelText = "Cancel",
  danger = false,
  modalProps = {},
}) => {
  const stateRef = {
    reason: "",
    isSubmitting: false,
  };

  /**
   * Modal content component
   */
  const Content = ({ updateModal }) => {
    const [reason, setReason] = useState("");

    const isValid =
      !requireReason || reason.trim().length >= minReasonLength;

    useEffect(() => {
      stateRef.reason = reason.trim();

      updateModal({
        okButtonProps: {
          disabled: !isValid || stateRef.isSubmitting,
        },
        cancelButtonProps: {
          disabled: stateRef.isSubmitting,
        },
      });
    }, [reason, isValid, updateModal]);

    return (
      <>
        {description && <p>{description}</p>}

        {requireReason && (
          <div style={{ marginTop: 16 }}>
            <Text strong>
              Reason <Text type="danger">*</Text>
            </Text>

            <Input.TextArea
              rows={3}
              value={reason}
              placeholder="Provide a reason"
              onChange={(e) => setReason(e.target.value)}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 4,
              }}
            >
              <Text type="danger" style={{ fontSize: 12 }}>
                Required (min {minReasonLength} characters)
              </Text>

              <Text
                type={isValid ? "success" : "secondary"}
                style={{ fontSize: 12 }}
              >
                {reason.trim().length}/{minReasonLength}
              </Text>
            </div>
          </div>
        )}
      </>
    );
  };

  const modal = Modal.confirm({
    title,
    okText,
    cancelText,
    centered: true,

    // hard safety defaults
    keyboard: false,
    maskClosable: false,

    // always primary
    okType: "primary",

    // control danger here
    okButtonProps: {
      danger: danger,
      disabled: requireReason,
      ...(modalProps.okButtonProps || {}),
    },

    content: (
      <Content updateModal={(props) => modal.update(props)} />
    ),

    onOk: async () => {
      // prevent multiple clicks
      if (stateRef.isSubmitting) return;

      stateRef.isSubmitting = true;

      modal.update({
        okButtonProps: {
          loading: true,
          disabled: true,
        },
        cancelButtonProps: {
          disabled: true,
        },
      });

      try {
        await onOk({
          reason: stateRef.reason || null,
        });
      } finally {
        stateRef.isSubmitting = false;
      }
    },

    ...modalProps,
  });
};
